import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";

import { getSignatureCertificate } from "@/lib/proposals.functions";

export const Route = createFileRoute("/p_/$token/certificate")({
  ssr: false,
  component: CertificatePage,
  head: () => ({
    meta: [
      { title: "Signature certificate · the Vibing Company®" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid gap-1 border-t border-neutral-200 py-3 sm:grid-cols-[220px_1fr] sm:gap-6">
      <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{label}</dt>
      <dd className="break-all text-sm text-neutral-900">{value}</dd>
    </div>
  );
}

function CertificatePage() {
  const { token } = Route.useParams();
  const load = useServerFn(getSignatureCertificate);
  const { data, isLoading } = useQuery({
    queryKey: ["signature-certificate", token],
    queryFn: () => load({ data: { token } }),
    retry: false,
  });

  if (isLoading) return <p className="p-10 text-sm">Preparing the certificate…</p>;
  if (!data) return <p className="p-10 text-sm">No signature has been recorded for this document.</p>;

  return (
    <div className="min-h-screen bg-neutral-200 py-10 print:bg-white print:py-0">
      <div className="pdf-toolbar mx-auto mb-6 flex max-w-[210mm] flex-wrap items-center justify-between gap-4 px-4">
        <a
          href={`/p/${token}`}
          className="text-xs text-neutral-700 underline underline-offset-4 hover:text-black"
        >
          ← Back to the proposal
        </a>
        <button
          type="button"
          onClick={() => window.print()}
          className="rounded-full bg-black px-5 py-2.5 text-sm font-medium text-white transition-[opacity,scale] hover:opacity-90 active:scale-[0.97]"
        >
          Download PDF
        </button>
      </div>

      <main className="pdf-sheet mx-auto w-[210mm] max-w-full bg-white p-12 text-neutral-900">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
          Signature certificate
        </p>
        <h1 className="mt-3 text-2xl font-medium tracking-tight">
          {data.proposalNumber} — {data.title}
        </h1>
        <p className="mt-3 max-w-prose text-sm text-neutral-600">
          Evidence record for the electronic signature applied to this agreement. The signature is a
          simple electronic signature under Art. 3(10) eIDAS and may not be denied legal effect
          solely because it is electronic (Art. 25(1) eIDAS).
        </p>

        <dl className="mt-8">
          <Row label="Signed by" value={data.signerName} />
          <Row label="Email" value={data.signerEmail ?? "—"} />
          <Row
            label="Email verified"
            value={data.emailVerifiedAt ? new Date(data.emailVerifiedAt).toUTCString() : "—"}
          />
          <Row label="Signed at (UTC)" value={new Date(data.signedAt).toUTCString()} />
          <Row label="Document fingerprint (SHA-256)" value={data.contentHash} />
          <Row
            label="Trusted timestamp"
            value={
              data.hasTimestampToken
                ? `RFC 3161 token issued by ${data.timestampAuthority}`
                : "Not available for this signature"
            }
          />
          <Row label="IP address" value={data.ipAddress ?? "—"} />
          <Row label="Browser" value={data.userAgent ?? "—"} />
          <Row label="Browser language" value={data.acceptLanguage ?? "—"} />
          <Row
            label="Agreed total"
            value={`${(data.totalCents / 100).toFixed(2)} ${data.currency.toUpperCase()}`}
          />
        </dl>

        <p className="mt-8 border-t border-neutral-200 pt-4 text-xs text-neutral-500">
          The fingerprint above covers the exact document text, line items, selected add-ons and
          billing details at the moment of signing. An immutable snapshot of that document is stored
          with the signature, so any later change to the proposal is detectable.
        </p>
      </main>
    </div>
  );
}
